import { produce } from 'immer'
import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import FileThumbnails from '~components/general/upload-files/file-thumbnails'
import FileUploadButton from '~components/general/upload-files/file-upload-button'
import Icon from '~components/ui/icon'
import TextArea from '~components/ui/text-input/text-area'
import useFilePreview from '~hooks/use-file-preview'
import { S3 } from '~models/s3'
import useCreateConsultation from '~repositories/hooks/use-create-consultation'
import useFileUpload from '~repositories/hooks/use-file-upload'
import { getIntakeSpecV2 } from '~repositories/hooks/use-intake-spec'
import getRootPath from '~utils/app/get-root-path'
import { cn } from '~utils/cn'
import { addFiles, deleteFiles, getVideoThumbnailUrls } from '~utils/file'

import { Navigate } from '@tanstack/react-router'

import ConsultationLayout from './components/layout'
import { ConsultationError, ConsultationSubmitting } from './containers'
import { useConsultationIntakeSpec } from './hooks/use-consultation-intake-spec'
import { useConsultationScreens } from './hooks/use-consultation-screens'
import { sanitizeCheckup } from './screens'
import { useConsultationData } from './store'

const MAX_PHOTO_NUM = 4
const MAX_VIDEO_NUM = 2

interface AttachmentContainerProps {
  metadata?: Record<string, string>
}

const Attachment = ({ metadata = {} }: AttachmentContainerProps) => {
  const [consultationData, setConsultationData] = useConsultationData()
  const [submittedConsultationId, setSubmittedConsultationId] = useState<number>(0)
  // 증상 화면에서 정한 문진 버전의 질문지 (제출 payload 도 이 버전을 따른다)
  const { data: intakeSpec } = useConsultationIntakeSpec()
  // 문진 v2: 제목·안내 문구는 질문지의 details (빼 버린 부위·계기 질문을 여기서 받는다)
  const details = getIntakeSpecV2(intakeSpec)?.details
  const screens = useConsultationScreens()
  const {
    mutate: createConsultation,
    isPending: isCreatingConsultation,
    isSuccess: isCreatingConsultationSuccess,
    error: createConsultationError,
  } = useCreateConsultation({
    onSuccess: data => {
      setSubmittedConsultationId(data.id)
    },
  })

  const textAreaRef = useCallback((node: HTMLTextAreaElement | null) => {
    node?.focus()
  }, [])

  const { previews, setPreview, resetPreview } = useFilePreview()

  const haveMediaFiles =
    consultationData.message?.photo_urls.length > 0 ||
    consultationData.message?.video_urls.length > 0

  const isPhotoUploadDisabled = consultationData.message?.photo_urls.length >= MAX_PHOTO_NUM
  const isVideoUploadDisabled = consultationData.message?.video_urls.length >= MAX_VIDEO_NUM

  const imageUploadProps = useFileUpload({
    type: 'image',
    maxNum: MAX_PHOTO_NUM,
    currentFileNum: consultationData.message?.photo_urls.length ?? 0,
    resourceType: S3.ResourceType.QUESTION_IMAGE,
    setPreviews: response => {
      setPreview(response, 'photos')
    },
    onChange: response => {
      setConsultationData(
        produce(({ message }) => {
          message.photo_urls = addFiles(MAX_PHOTO_NUM, message.photo_urls, response)
        })
      )
      resetPreview()
    },
  })

  const videoUploadProps = useFileUpload({
    type: 'video',
    maxNum: MAX_VIDEO_NUM,
    currentFileNum: consultationData.message.video_urls.length ?? 0,
    resourceType: S3.ResourceType.QUESTION_VIDEO,
    setPreviews: response => {
      setPreview(response, 'videos')
    },
    onChange: response => {
      setConsultationData(
        produce(({ message }) => {
          message.video_urls = addFiles(MAX_VIDEO_NUM, message.video_urls, response)
        })
      )
      resetPreview()
    },
  })

  const videoThumbnailUrls = useMemo(
    () => getVideoThumbnailUrls(consultationData.message?.video_urls ?? []),
    [consultationData.message?.video_urls]
  )

  if (!consultationData) {
    throw new Error('No consultation data')
  }

  const onTextChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setConsultationData(
        produce(draft => {
          if (!draft.message) {
            draft.message = {}
          }
          draft.message.text = event.target.value
        })
      )
    },
    [setConsultationData]
  )

  const onDeleteFile = useCallback(
    (index: number, target: 'video' | 'image') => {
      setConsultationData(
        produce(({ message }) => {
          if (target === 'video') {
            message.video_urls = deleteFiles(message.video_urls, index)
          }

          if (target === 'image') {
            message.photo_urls = deleteFiles(message.photo_urls, index)
          }
        })
      )
    },
    [setConsultationData]
  )

  const startConsultation = useCallback(() => {
    const dto = consultationData.toDto()
    // 예전 임시 답에 남은 삭제된 화면의 키와 건너뛴 안전 질문의 답은 보내지 않는다
    // 문진 v2 면 고른 증상에 { intake_version: 2, category?, safety } 만 보낸다
    // 띄운 응급 안내의 가장 높은 단계도 함께 보낸다(intake_alert_shown)
    dto.checkup = sanitizeCheckup(
      dto.checkup,
      intakeSpec,
      consultationData.pet,
      consultationData.intake_alert_level
    )
    dto.metadata = metadata
    createConsultation(dto)
  }, [consultationData, createConsultation, intakeSpec, metadata])

  if (submittedConsultationId && isCreatingConsultationSuccess) {
    // @TODO: Submitted 같은 경우는 별도 페이지로 파서 이동할 수 있게 해야함.
    return (
      <Navigate
        // @ts-expect-error: getRootPath assert 가능
        to={`${getRootPath()}/ask-a-vet/submitted`}
        search={{
          consultationId: submittedConsultationId.toString(),
        }}
      />
    )
  }

  if (createConsultationError) {
    return <ConsultationError onRetry={startConsultation} />
  }

  if (isCreatingConsultation) {
    return <ConsultationSubmitting progress={0} />
  }

  return (
    <ConsultationLayout
      title={details?.question ?? `Add more details\nabout your pet's issues`}
      onNextPress={startConsultation}
      progress={screens && { current: screens.total, total: screens.total }}
      nextButtonDisabled={!consultationData.message?.text}
      required={true}
    >
      <TextArea
        type="box"
        ref={textAreaRef}
        value={consultationData.message?.text}
        onChange={onTextChange}
        className="h-36 w-full py-3 mb-5"
        placeholder={
          details?.placeholder ?? 'The more details you provide, the better the response.'
        }
        maxLength={1500}
      />
      {haveMediaFiles ? (
        <div
          className={cn(
            'no-scrollbar mb-4 flex overflow-y-hidden overflow-x-auto',
            videoThumbnailUrls.length +
              previews.videos.length +
              previews.photos.length +
              (consultationData.message?.photo_urls?.length ?? 0) >
              1 && 'flex-gap-x-2'
          )}
        >
          <FileThumbnails images={previews.videos} />
          <FileThumbnails
            images={videoThumbnailUrls}
            onDelete={(index: number) => onDeleteFile(index, 'video')}
            isVideo
          />
          <FileThumbnails images={previews.photos} />
          <FileThumbnails
            images={consultationData.message?.photo_urls ?? []}
            onDelete={(index: number) => onDeleteFile(index, 'image')}
          />
        </div>
      ) : null}
      <div className="flex items-center flex-gap-x-2 py-5">
        <FileUploadButton
          {...imageUploadProps}
          disabled={isPhotoUploadDisabled}
          className="flex flex-gap-x-1 dark:text-primary-300 dark:border-primary-300"
        >
          <Icon
            name="camera_sm"
            className={cn(
              'size-5 fill-primary-600 dark:fill-primary-300',
              isPhotoUploadDisabled && 'fill-gray-400'
            )}
          />
          <div>
            Photo {consultationData.message.photo_urls.length ?? 0}/{MAX_PHOTO_NUM}
          </div>
        </FileUploadButton>
        <FileUploadButton
          {...videoUploadProps}
          disabled={isVideoUploadDisabled}
          className="flex flex-gap-x-1 dark:text-primary-300 dark:border-primary-300"
        >
          <Icon
            name="video_sm"
            className={cn(
              'size-5 fill-primary-600 dark:fill-primary-300',
              isVideoUploadDisabled && 'fill-gray-400'
            )}
          />
          <div>
            Video {consultationData.message.video_urls.length ?? 0}/{MAX_VIDEO_NUM}
          </div>
        </FileUploadButton>
      </div>
    </ConsultationLayout>
  )
}

export default Attachment
